import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useCourses } from '../contexts/CourseContext';
import { useNotices } from '../contexts/NoticeContext';

const Home: React.FC = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const { courses } = useCourses();
  const { notices } = useNotices();

  const slides = [
    {
      id: 1,
      title: "Global Talent Center",
      subtitle: "For Future Engineers",
      image: "https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=1920&h=800&fit=crop"
    },
    {
      id: 2,
      title: "Professional Education",
      subtitle: "AI & Engineering Excellence",
      image: "https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?w=1920&h=800&fit=crop"
    },
    {
      id: 3,
      title: "Global Competency",
      subtitle: "Building Tomorrow's Leaders",
      image: "https://images.unsplash.com/photo-1552664730-d307ca884978?w=1920&h=800&fit=crop"
    },
    {
      id: 4,
      title: "Innovation Hub",
      subtitle: "Where Ideas Meet Reality",
      image: "https://images.unsplash.com/photo-1531482615713-2afd69097998?w=1920&h=800&fit=crop"
    },
    {
      id: 5,
      title: "Career Development",
      subtitle: "Your Success, Our Mission",
      image: "https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=1920&h=800&fit=crop"
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [slides.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  return (
    <div className="relative">
      {/* Main Banner Section with Header */}
      <div className="visual_wrap relative h-screen">
        {/* Header - positioned absolutely on top of banner */}
        <Header isTransparent={true} />

        {/* Title and Counter */}
        <div className="absolute top-32 left-12 z-20 text-white">
          <div className="main-counter mb-4">
            <div className="flex items-center gap-2 text-sm">
              <span className="font-bold text-2xl">{String(currentSlide + 1).padStart(2, '0')}</span>
              <div className="w-12 h-[2px] bg-white"></div>
              <span className="opacity-70">{String(slides.length).padStart(2, '0')}</span>
            </div>
          </div>
          <h2 className="text-6xl font-bold leading-tight ml-0">
            <strong className="text-green-400">D</strong>ongducheon<br />
            <strong className="text-green-400">G</strong>lobal <strong className="text-green-400">T</strong>alent<br />
            <strong className="text-green-400">C</strong>enter
          </h2>
        </div>

        {/* Quick Menu */}
        <div className="absolute bottom-0 left-0 right-0 z-20">
          <div className="max-w-7xl mx-auto px-8 pb-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Link to="/programs" className="bg-white/95 backdrop-blur-sm p-6 rounded-xl hover:bg-blue-50 transition-all group cursor-pointer">
                <h3 className="font-bold text-lg mb-2 group-hover:text-blue-600">교육프로그램</h3>
                <p className="text-sm text-gray-600">AI / 공학 전문 과정 /<br />맞춤형 교육</p>
              </Link>
              <Link to="/support" className="bg-white/95 backdrop-blur-sm p-6 rounded-xl hover:bg-blue-50 transition-all group cursor-pointer">
                <h3 className="font-bold text-lg mb-2 group-hover:text-blue-600">취업지원</h3>
                <p className="text-sm text-gray-600">Career<br />Management System</p>
              </Link>
              <Link to="/notice" className="bg-white/95 backdrop-blur-sm p-6 rounded-xl hover:bg-blue-50 transition-all group cursor-pointer">
                <h3 className="font-bold text-lg mb-2 group-hover:text-blue-600">공지사항</h3>
                <p className="text-sm text-gray-600">센터 소식 및<br />교육 일정 안내</p>
              </Link>
              <Link to="/contact" className="bg-white/95 backdrop-blur-sm p-6 rounded-xl hover:bg-blue-50 transition-all group cursor-pointer">
                <h3 className="font-bold text-lg mb-2 group-hover:text-blue-600">센터 안내</h3>
                <p className="text-sm text-gray-600">편리하고 쾌적한<br />교육 및 회의 공간</p>
              </Link>
            </div>
          </div>
        </div>

        {/* Slider */}
        <div className="main_visual_area absolute inset-0 h-full overflow-hidden">
          <div className="mainSlide relative h-full">
            <div className="relative h-full">
              {slides.map((slide, index) => (
                <div
                  key={slide.id}
                  className={`absolute inset-0 transition-opacity duration-1000 ${
                    index === currentSlide ? 'opacity-100' : 'opacity-0'
                  }`}
                >
                  <img
                    src={slide.image}
                    alt={slide.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/50"></div>
                </div>
              ))}
            </div>

            {/* Slide Controls */}
            <div className="absolute top-1/2 -translate-y-1/2 left-8 right-8 flex justify-between">
              <button
                onClick={prevSlide}
                className="bg-white/20 backdrop-blur-sm text-white p-3 rounded-full hover:bg-white/30 transition-all"
              >
                <ChevronLeftIcon className="h-6 w-6" />
              </button>
              <button
                onClick={nextSlide}
                className="bg-white/20 backdrop-blur-sm text-white p-3 rounded-full hover:bg-white/30 transition-all"
              >
                <ChevronRightIcon className="h-6 w-6" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Section */}
      <div className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-8">
          <div className="grid md:grid-cols-2 gap-12">
            {/* 신규 교육·프로그램 */}
            <div className="bg-white rounded-lg shadow-sm p-8">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-800">신규 교육·프로그램</h2>
                <Link
                  to="/courses"
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
                >
                  더보기
                  <ChevronRightIcon className="h-4 w-4" />
                </Link>
              </div>

              {courses.slice(0, 1).map((course) => (
                <Link
                  key={course.id}
                  to={`/courses/${course.id}`}
                  className="block group"
                >
                  {/* 썸네일 */}
                  <div className="relative mb-4 rounded-lg overflow-hidden">
                    <span className={`absolute top-3 left-3 z-10 text-white text-xs font-semibold px-3 py-1 rounded-full ${
                      course.status === 'recruiting' ? 'bg-green-600' : 'bg-blue-600'
                    }`}>
                      {course.status === 'recruiting' ? '모집중' : 'NEW'}
                    </span>
                    <img
                      src={course.courseImage || 'https://www.bgv.co.kr/sm/upload/files/kor_popup_d8c9e8ccc4733fabdb477011bd02b93c.png'}
                      alt={course.courseName}
                      className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>

                  <div className="mb-3">
                    <span className="inline-block bg-gray-100 text-gray-700 text-xs font-medium px-3 py-1 rounded-full">
                      교육
                    </span>
                  </div>

                  <h3 className="text-xl font-bold text-gray-800 mb-4 group-hover:text-blue-600 transition-colors">
                    {course.courseName}
                  </h3>

                  <div className="space-y-2 text-sm text-gray-600">
                    <p className="flex items-center gap-2">
                      <span className="font-medium">교육기간:</span>
                      <span>{course.courseStartDate} ~ {course.courseEndDate}</span>
                    </p>
                    <p className="flex items-center gap-2">
                      <span className="font-medium">장소:</span>
                      <span>{course.institution} {course.location}</span>
                    </p>
                  </div>

                  <div className="mt-4 pt-4 border-t border-gray-100 flex gap-2">
                    <button className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                      ❤️ {course.currentApplicants}
                    </button>
                    <button className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                      📌 관심 교육
                    </button>
                  </div>
                </Link>
              ))}
            </div>

            {/* 공지사항 */}
            <div className="bg-white rounded-lg shadow-sm p-8">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-800">공지사항</h2>
                <Link
                  to="/notice"
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
                >
                  더보기
                  <ChevronRightIcon className="h-4 w-4" />
                </Link>
              </div>

              <ul className="divide-y divide-gray-100">
                {notices.slice(0, 4).map((notice) => (
                  <li key={notice.id}>
                    <Link
                      to={`/notice/${notice.id}`}
                      className="block py-4 px-2 hover:bg-gray-50 transition-colors group"
                    >
                      <div className="flex justify-between items-center gap-4">
                        <h3 className="text-base font-medium text-gray-800 group-hover:text-blue-600 transition-colors flex-1 truncate">
                          {notice.title}
                        </h3>
                        <p className="text-sm text-gray-400 whitespace-nowrap">
                          {notice.date}
                        </p>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Home;